import React, { useEffect, useMemo, useState } from "react";
import { useFilterContext } from "../../../contexts/FilterContext";
import { useQuery } from "@tanstack/react-query";
import usePost from "../../../hooks/usePost";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const Table = () => {
  const { filters } = useFilterContext();
  const { makeRequest } = usePost();
  const [tableData, setTableData] = useState([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["tableData", filters?.startDate, filters?.endDate],
    queryFn: () =>
      makeRequest("/BVNPORTAL_API/api/tableDatabaseCount.php", {
        dateRange: `${filters?.startDate} - ${filters?.endDate}`,
      }),
    enabled: !!filters?.startDate && !!filters?.endDate,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  useMemo(() => {
    if (data?.data) {
      const rows = [];
      for (const time in data.data) {
        for (const service in data.data[time]) {
          rows.push({
            time: time.replace(/_/g, ":"),
            service,
            ...data.data[time][service],
          });
        }
      }
      setTableData(rows);
    }
  }, [data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "time",
        header: "Time Slot",
      },
      {
        accessorKey: "service",
        header: "Service",
      },
      {
        accessorKey: "GLO",
        header: "GLO",
      },
      {
        accessorKey: "MTN",
        header: "MTN",
      },
      {
        accessorKey: "AIRTEL",
        header: "AIRTEL",
      },
      {
        accessorKey: "ETISALAT",
        header: "ETISALAT",
      },
    ],
    []
  );

  const table = useReactTable({
    data: tableData,
    columns,
    initialState: { pagination: { pageSize: 20 } },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const formatTableHeader = (header) => {
    if (!header) return "";
    const dateRangeMatch = header.match(
      /(\d{4}-\d{2}-\d{2})(?:\s*to\s*(\d{4}-\d{2}-\d{2}))?/
    );
    if (!dateRangeMatch) return header;

    const startDate = new Date(dateRangeMatch[1]);
    const endDate = dateRangeMatch[2] ? new Date(dateRangeMatch[2]) : null;

    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedStart = startDate.toLocaleDateString("en-US", options);
    const formattedEnd = endDate
      ? endDate.toLocaleDateString("en-US", options)
      : null;

    return endDate
      ? `Hit count from ${formattedStart} to ${formattedEnd}`
      : `Hit count from ${formattedStart}`;
  };

  const exportToXLSX = () => {
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Table Data");
    XLSX.writeFile(
      workbook,
      `table_data_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [columns.map((col) => col.header)],
      body: tableData.map((row) =>
        columns.map((col) => row[col.accessorKey] || "")
      ),
      theme: "striped",
      headStyles: { fillColor: [59, 130, 246] },
    });

    doc.save(`table_data_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  if (isLoading)
    return (
      <div className="p-6 max-w-7xl mx-auto">
        {/* Loading Spinner */}
        <div className="flex justify-center mb-4">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div className="flex justify-between items-center mb-6">
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse"></div>
          <div className="space-x-2">
            <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
            <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
          </div>
        </div>
        <div className="overflow-x-auto bg-white rounded-xl shadow-lg border border-gray-100">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((_, index) => (
                  <th
                    key={index}
                    className="px-6 py-4 text-left text-sm font-semibold text-gray-900 uppercase tracking-wider"
                  >
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {[...Array(10)].map((_, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {columns.map((_, colIndex) => (
                    <td
                      key={colIndex}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                    >
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-center mt-6 space-x-2">
          <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="h-10 w-12 bg-gray-200 rounded-md animate-pulse"
            ></div>
          ))}
          <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
        </div>
      </div>
    );
  if (error)
    return (
      <div className="text-center py-4 text-red-500">
        Error: {error.message}
      </div>
    );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        {data?.table_header && (
          <h2 className="text-2xl font-semibold text-gray-800">
            {formatTableHeader(data.table_header)}
          </h2>
        )}
        <div className="space-x-2">
          <button
            onClick={exportToXLSX}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Export to XLSX
          </button>
          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Export to PDF
          </button>
        </div>
      </div>
      <div className="overflow-x-auto bg-white rounded-xl shadow-lg border border-gray-100">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-4 text-left text-sm font-semibold text-gray-900 uppercase tracking-wider"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination controls */}
      <div className="flex items-center justify-center mt-6 space-x-2">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        {table.getPageCount() > 0 &&
          Array.from({ length: table.getPageCount() }, (_, index) => (
            <button
              key={index}
              onClick={() => table.setPageIndex(index)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                table.getState().pagination.pageIndex === index
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
            >
              {index + 1}
            </button>
          ))}
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default Table;